import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
    ArrowLeft,
    Music,
    Sparkles,
    Loader2,
    AlertCircle,
    Lock,
    Download,
    RefreshCw,
    Disc3,
} from "lucide-react";

const POLL_MS = 5000;

const STYLE_PRESETS = [
    "Pop", "Rock", "Hip-Hop", "R&B", "Lo-fi", "EDM",
    "Country", "Jazz", "Acoustic", "Cinematic", "Reggaeton", "Metal",
];

export default function TryMusicCreator() {
    const { user, loading: authLoading } = useAuth();
    const [prompt, setPrompt] = useState("");
    const [lyrics, setLyrics] = useState("");
    const [style, setStyle] = useState("Pop");
    const [instrumental, setInstrumental] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [job, setJob] = useState(null);
    const [error, setError] = useState("");

    // Poll job
    useEffect(() => {
        if (!job?.id) return;
        if (job.status === "COMPLETED" || job.status === "FAILED") return;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/music/jobs/${job.id}`);
                setJob(data);
            } catch {
                setJob((j) => ({ ...j, _ticks: (j._ticks || 0) + 1 }));
            }
        }, POLL_MS);
        return () => clearTimeout(t);
    }, [job]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!prompt.trim() && !lyrics.trim()) {
            return setError("Add a prompt or lyrics to describe the song");
        }
        setSubmitting(true);
        setJob(null);
        try {
            const { data } = await api.post("/music/jobs", {
                prompt,
                lyrics,
                music_style: style,
                make_instrumental: instrumental,
            });
            setJob(data);
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    const reset = useCallback(() => { setJob(null); setError(""); }, []);

    const isLoggedIn = user && user !== false;
    const inProgress = job && (job.status === "IN_QUEUE" || job.status === "IN_PROGRESS");

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <Link to="/" data-testid="try-music-back-link" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                Back to Endfb
            </Link>

            <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-vibrantblue border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg flex-shrink-0">
                    <Music className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.5} />
                </div>
                <div>
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Music <span className="text-vibrantorange">Creator</span>
                    </h1>
                    <p className="font-body text-ink/70 mt-1">
                        Drop lyrics, pick a style, and AI writes you a song.
                    </p>
                </div>
            </div>

            {authLoading ? (
                <div className="font-heading font-bold text-ink/60">Loading...</div>
            ) : !isLoggedIn ? (
                <div data-testid="music-login-gate" className="brutal-card p-8 bg-white max-w-lg">
                    <div className="w-12 h-12 bg-vibrantorange border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal mb-4">
                        <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="font-heading font-black text-3xl text-ink tracking-tighter mb-2">Log in to create music</h2>
                    <p className="font-body text-ink/70 mb-5">
                        Sign in to generate songs. It's free for logged-in users.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/login" className="brutal-btn-primary">Log in</Link>
                        <Link to="/register" className="brutal-btn-secondary">Create account</Link>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                    {/* Form */}
                    <form onSubmit={onSubmit} className="lg:col-span-6 space-y-5" data-testid="music-form">
                        <div>
                            <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2 block">Prompt</label>
                            <textarea
                                data-testid="music-prompt-input"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="e.g. an upbeat indie pop song about late-night drives"
                                rows={3}
                                maxLength={280}
                                className="brutal-input resize-none"
                            />
                            <p className="mt-1 font-body text-[10px] text-ink/40 text-right">{prompt.length}/280</p>
                        </div>

                        <div>
                            <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2 block">Lyrics (optional)</label>
                            <textarea
                                data-testid="music-lyrics-input"
                                value={lyrics}
                                onChange={(e) => setLyrics(e.target.value)}
                                placeholder="Add your own lyrics or leave blank to let the AI write them"
                                rows={5}
                                maxLength={2000}
                                className="brutal-input resize-none font-mono text-sm"
                            />
                            <p className="mt-1 font-body text-[10px] text-ink/40 text-right">{lyrics.length}/2000</p>
                        </div>

                        <div>
                            <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2 block">Music Style</label>
                            <div className="flex flex-wrap gap-2" data-testid="music-style-presets">
                                {STYLE_PRESETS.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => setStyle(s)}
                                        data-testid={`music-style-${s.replace(/\W/g, "")}`}
                                        className={`font-heading font-bold text-xs uppercase tracking-widest border-[3px] border-ink rounded-full px-3 py-1.5 transition-all ${style === s ? "bg-ink text-white shadow-brutal" : "bg-white text-ink hover:bg-vibrantyellow"}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                            <input
                                type="text"
                                value={style}
                                onChange={(e) => setStyle(e.target.value)}
                                placeholder="Or type a custom style"
                                maxLength={64}
                                data-testid="music-style-input"
                                className="brutal-input mt-3 text-sm"
                            />
                        </div>

                        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="music-instrumental-toggle">
                            <input
                                type="checkbox"
                                checked={instrumental}
                                onChange={(e) => setInstrumental(e.target.checked)}
                                className="w-5 h-5 border-[3px] border-ink rounded accent-vibrantorange"
                            />
                            <span className="font-heading font-bold text-sm text-ink">Instrumental only (no vocals)</span>
                        </label>

                        {error && (
                            <div data-testid="music-error" className="flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-3">
                                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                                <span className="font-body font-semibold text-sm">{error}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting || inProgress}
                            className="brutal-btn-blue w-full text-base py-3.5"
                            data-testid="music-submit-button"
                        >
                            <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.5} />
                            {submitting ? "Submitting..." : inProgress ? "Generating..." : "Generate music"}
                        </button>

                        <p className="font-body text-xs text-ink/50 text-center">
                            Powered by MusicGPT · Generation takes ~1-3 minutes · You'll get 2 versions per request.
                        </p>
                    </form>

                    {/* Result */}
                    <div className="lg:col-span-6">
                        <div data-testid="music-result-panel" className="brutal-card p-5 sm:p-6 bg-white sticky top-24">
                            <h2 className="font-heading font-black text-xl text-ink tracking-tight mb-4 flex items-center gap-2">
                                Result
                                {job && (
                                    <span data-testid="music-status" className={`text-xs font-heading font-bold uppercase tracking-widest border-[3px] border-ink rounded-full px-2 py-0.5 ${job.status === "COMPLETED" ? "bg-vibrantyellow text-ink" : job.status === "FAILED" ? "bg-vibrantpink text-white" : "bg-vibrantblue text-white"}`}>
                                        {job.status === "COMPLETED" ? "Done!" : job.status === "FAILED" ? "Failed" : "Generating"}
                                    </span>
                                )}
                            </h2>

                            {!job ? (
                                <div data-testid="music-result-empty" className="bg-cream border-[3px] border-dashed border-ink rounded-xl p-12 flex flex-col items-center justify-center">
                                    <Disc3 className="w-12 h-12 text-ink/40 mb-3" strokeWidth={2.5} />
                                    <p className="font-heading font-bold text-sm uppercase tracking-widest text-ink/50">Your tracks will appear here</p>
                                </div>
                            ) : job.status === "COMPLETED" && job.tracks?.length ? (
                                <div className="space-y-4">
                                    {job.tracks.map((t, i) => (
                                        <div key={t.url} data-testid={`music-track-${i}`} className="bg-cream border-[3px] border-ink rounded-xl p-4 shadow-brutal">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="w-7 h-7 bg-vibrantorange border-2 border-ink rounded-full flex items-center justify-center">
                                                    <span className="font-heading font-black text-xs text-white">{i + 1}</span>
                                                </div>
                                                <div className="font-heading font-bold text-sm text-ink truncate flex-1">{t.title}</div>
                                                <a
                                                    href={t.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="brutal-btn-white text-[10px] py-1.5 px-2"
                                                    data-testid={`music-download-${i}`}
                                                >
                                                    <Download className="w-3 h-3 mr-1" strokeWidth={2.5} />
                                                    Save
                                                </a>
                                            </div>
                                            <audio
                                                controls
                                                src={t.url}
                                                data-testid={`music-audio-${i}`}
                                                className="w-full"
                                                preload="metadata"
                                            />
                                        </div>
                                    ))}
                                    <button type="button" onClick={reset} data-testid="music-reset-button" className="brutal-btn-secondary text-xs py-2 px-3">
                                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                                        Make another
                                    </button>
                                </div>
                            ) : job.status === "FAILED" ? (
                                <div data-testid="music-result-failed" className="bg-vibrantpink/15 border-[3px] border-vibrantpink rounded-xl p-4">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-5 h-5 mt-0.5 text-vibrantpink flex-shrink-0" strokeWidth={2.5} />
                                        <div>
                                            <div className="font-heading font-bold text-ink mb-1">Generation failed</div>
                                            <p className="font-body text-sm text-ink/80 break-words">{job.error || "Unknown error"}</p>
                                        </div>
                                    </div>
                                    <button type="button" onClick={reset} className="brutal-btn-white text-xs py-2 px-3 mt-3" data-testid="music-retry-button">
                                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                                        Try again
                                    </button>
                                </div>
                            ) : (
                                <div data-testid="music-result-loading" className="bg-cream border-[3px] border-ink rounded-xl p-10 flex flex-col items-center justify-center">
                                    <Loader2 className="w-10 h-10 text-vibrantblue mb-3 animate-spin" strokeWidth={2.5} />
                                    <p className="font-heading font-black text-lg text-ink">Composing your song...</p>
                                    <p className="font-body text-xs text-ink/60 mt-1">MusicGPT is writing and producing. ~1-3 min.</p>
                                    {job?.eta ? (
                                        <p className="font-body text-[10px] text-ink/40 mt-2">ETA: {job.eta}s</p>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
